/**
 * 폴더 사이드바 컴포넌트
 * 폴더 트리 구조 표시 및 관리
 */

import React, { useState } from 'react';
import {
  FolderOpen,
  Folder,
  FolderPlus,
  Star,
  Clock,
  Archive,
  Trash2,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Edit2,
  Palette,
  Trash,
  BookOpen,
  Briefcase,
  Heart,
  Shield,
  Code,
  Music,
  Camera,
  MapPin,
  ShoppingBag,
  Users,
  Home,
  FileText
} from 'lucide-react';
import { useFolders } from '../../contexts/FolderContext';
import { useSecureNotes } from '../../contexts/SecureNotesContext';
import { Button } from '../UI';
import './FolderSidebar.css';

function FolderSidebar({ isOpen = false }) {
  const {
    folderTree,
    selectedFolder,
    selectFolder,
    createFolder,
    updateFolder,
    deleteFolder,
    moveNote,
    SYSTEM_FOLDERS
  } = useFolders();

  const { notes } = useSecureNotes();
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);

  // 폴더 토글
  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // 폴더별 메모 개수 계산
  const getFolderNoteCount = (folderId) => {
    if (folderId === SYSTEM_FOLDERS.ALL) {
      // 삭제되지 않은 모든 메모
      return notes.filter(note => !note.isDeleted).length;
    } else if (folderId === SYSTEM_FOLDERS.FAVORITES) {
      return notes.filter(note => note.isFavorite && !note.isDeleted).length;
    } else if (folderId === SYSTEM_FOLDERS.RECENT) {
      // 최근 7일 이내 수정된 메모 (삭제되지 않은 것만)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return notes.filter(note =>
        new Date(note.updatedAt) > sevenDaysAgo && !note.isDeleted
      ).length;
    } else if (folderId === SYSTEM_FOLDERS.ARCHIVED) {
      return notes.filter(note => note.isArchived && !note.isDeleted).length;
    } else if (folderId === SYSTEM_FOLDERS.TRASH) {
      return notes.filter(note => note.isDeleted).length;
    } else {
      return notes.filter(note => note.folderId === folderId && !note.isDeleted).length;
    }
  };

  // 아이콘 렌더링
  const renderFolderIcon = (folder) => {
    const IconComponent = {
      folder: expandedFolders.has(folder.id) ? FolderOpen : Folder,
      star: Star,
      clock: Clock,
      archive: Archive,
      trash: Trash2,
      book: BookOpen,
      briefcase: Briefcase,
      heart: Heart,
      shield: Shield,
      code: Code,
      music: Music,
      camera: Camera,
      map: MapPin,
      shopping: ShoppingBag,
      users: Users,
      home: Home,
      file: FileText
    }[folder.icon] || Folder;

    return <IconComponent size={18} style={{ color: folder.color }} />;
  };

  // 드래그 앤 드롭 핸들러
  const handleDragStart = (e, noteId) => {
    e.dataTransfer.setData('noteId', noteId);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    const noteId = e.dataTransfer.getData('noteId');

    if (noteId && folderId) {
      try {
        await moveNote(noteId, folderId);
        setDragOverFolder(null);
      } catch (error) {
        console.error('Error moving note:', error);
      }
    }
  };

  // 컨텍스트 메뉴 핸들러
  const handleContextMenu = (e, folder) => {
    e.preventDefault();

    if (!folder.system) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        folder
      });
    }
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // 폴더 렌더링 (재귀)
  const renderFolder = (folder, level = 0, index = null) => {
    const isSelected = selectedFolder === folder.id;
    const isExpanded = expandedFolders.has(folder.id);
    const hasChildren = folder.children && folder.children.length > 0;
    const noteCount = getFolderNoteCount(folder.id);
    const isDragOver = dragOverFolder === folder.id;

    // 최상위 레벨(level === 0)일 때만 인덱스 사용
    const currentIndex = index;

    return (
      <div key={folder.id} className="folder-item-wrapper">
        <div
          className={`folder-item ${isSelected ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            console.log('Selecting folder:', folder.id, folder.name);
            selectFolder(folder.id);
          }}
          onContextMenu={(e) => handleContextMenu(e, folder)}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, folder.id)}
        >
          {hasChildren && (
            <button
              className="folder-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}

          <div className="folder-icon">
            {renderFolderIcon(folder)}
          </div>

          <span className="folder-name">{folder.name}</span>

          <span className="folder-count">{noteCount}</span>

          {!folder.system ? (
            <button
              className="folder-menu-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e, folder);
              }}
            >
              <MoreVertical size={14} />
            </button>
          ) : (
            <span className="folder-menu-spacer"></span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="folder-children">
            {folder.children.map(child => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`folder-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h3>폴더</h3>
        <Button
          size="small"
          variant="ghost"
          onClick={() => setShowNewFolderModal(true)}
          title="새 폴더"
        >
          <FolderPlus size={18} />
        </Button>
      </div>

      <div className="folder-list">
        {folderTree.map((folder, idx) => renderFolder(folder, 0, idx + 1))}
      </div>

      {/* 컨텍스트 메뉴 */}
      {contextMenu && (
        <>
          <div className="context-menu-overlay" onClick={closeContextMenu} />
          <div
            className="context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button onClick={() => {
              setEditingFolder(contextMenu.folder);
              closeContextMenu();
            }}>
              <Edit2 size={14} />
              이름 변경
            </button>
            <button onClick={() => {
              setEditingFolder({ ...contextMenu.folder, editMode: 'color' });
              closeContextMenu();
            }}>
              <Palette size={14} />
              색상 변경
            </button>
            <button
              className="danger"
              onClick={() => {
                if (confirm(`"${contextMenu.folder.name}" 폴더를 삭제하시겠습니까? 폴더 내 메모는 "모든 메모"로 이동됩니다.`)) {
                  deleteFolder(contextMenu.folder.id);
                  closeContextMenu();
                }
              }}
            >
              <Trash size={14} />
              삭제
            </button>
          </div>
        </>
      )}

      {/* 새 폴더 모달 */}
      {showNewFolderModal && (
        <NewFolderModal
          onClose={() => setShowNewFolderModal(false)}
          onCreate={createFolder}
        />
      )}

      {/* 폴더 편집 모달 */}
      {editingFolder && (
        <EditFolderModal
          folder={editingFolder}
          onClose={() => setEditingFolder(null)}
          onUpdate={updateFolder}
        />
      )}
    </div>
  );
}

// 색상 팔레트
const FOLDER_COLORS = [
  '#4A90E2', // Blue
  '#50C878', // Emerald
  '#FFD700', // Gold
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#9B59B6', // Purple
  '#F39C12', // Orange
  '#95A5A6', // Gray
  '#E91E63', // Pink
  '#00BCD4', // Cyan
  '#8BC34A', // Light Green
  '#795548'  // Brown
];

// 아이콘 옵션
const FOLDER_ICONS = [
  { value: 'folder', label: '폴더', Icon: Folder },
  { value: 'star', label: '별', Icon: Star },
  { value: 'book', label: '책', Icon: BookOpen },
  { value: 'briefcase', label: '업무', Icon: Briefcase },
  { value: 'heart', label: '하트', Icon: Heart },
  { value: 'shield', label: '보안', Icon: Shield },
  { value: 'code', label: '코드', Icon: Code },
  { value: 'music', label: '음악', Icon: Music },
  { value: 'camera', label: '사진', Icon: Camera },
  { value: 'map', label: '장소', Icon: MapPin },
  { value: 'shopping', label: '쇼핑', Icon: ShoppingBag },
  { value: 'users', label: '사람', Icon: Users },
  { value: 'home', label: '홈', Icon: Home },
  { value: 'file', label: '문서', Icon: FileText }
];

// 새 폴더 생성 모달
function NewFolderModal({ onClose, onCreate }) {
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#4A90E2');
  const [folderIcon, setFolderIcon] = useState('folder');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!folderName.trim()) {
      setError('폴더 이름을 입력해주세요.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      console.log('Creating folder with data:', {
        name: folderName,
        color: folderColor,
        icon: folderIcon
      });

      await onCreate({
        name: folderName.trim(),
        color: folderColor,
        icon: folderIcon
      });

      console.log('Folder created successfully');
      onClose();
    } catch (error) {
      console.error('Error creating folder:', error);
      setError(error.message || '폴더 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content folder-modal" onClick={e => e.stopPropagation()}>
        <h3>새 폴더</h3>
        <div className="form-group">
          <label>폴더 이름</label>
          <input
            type="text"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="폴더 이름 입력"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>아이콘</label>
          <div className="icon-selector">
            {FOLDER_ICONS.map(({ value, label, Icon }) => (
              <button
                key={value}
                className={`icon-option ${folderIcon === value ? 'selected' : ''}`}
                onClick={() => setFolderIcon(value)}
                title={label}
                type="button"
              >
                <Icon size={20} style={{ color: folderColor }} />
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>색상</label>
          <div className="color-selector">
            {FOLDER_COLORS.map(color => (
              <button
                key={color}
                className={`color-option ${folderColor === color ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setFolderColor(color)}
                type="button"
                aria-label={color}
              />
            ))}
            <input
              type="color"
              value={folderColor}
              onChange={(e) => setFolderColor(e.target.value)}
              className="color-picker"
            />
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="form-error" style={{
            color: 'var(--danger-color)',
            fontSize: '14px',
            marginTop: '12px',
            padding: '8px 12px',
            background: 'rgba(220, 53, 69, 0.1)',
            borderRadius: '6px',
            border: '1px solid rgba(220, 53, 69, 0.3)'
          }}>
            {error}
          </div>
        )}

        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={creating}>
            취소
          </Button>
          <Button onClick={handleCreate} disabled={creating || !folderName.trim()}>
            {creating ? '생성 중...' : '생성'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 폴더 편집 모달
function EditFolderModal({ folder, onClose, onUpdate }) {
  const [folderName, setFolderName] = useState(folder.name);
  const [folderColor, setFolderColor] = useState(folder.color || '#4A90E2');
  const [folderIcon, setFolderIcon] = useState(folder.icon || 'folder');
  const isColorMode = folder.editMode === 'color';

  const handleUpdate = async () => {
    if (!isColorMode && !folderName.trim()) return;

    try {
      await onUpdate(folder.id, {
        ...(isColorMode ? {} : { name: folderName }),
        color: folderColor,
        icon: folderIcon
      });
      onClose();
    } catch (error) {
      console.error('Error updating folder:', error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content folder-modal" onClick={e => e.stopPropagation()}>
        <h3>{isColorMode ? '폴더 꾸미기' : '폴더 편집'}</h3>
        {!isColorMode && (
          <div className="form-group">
            <label>폴더 이름</label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="폴더 이름 입력"
              autoFocus
            />
          </div>
        )}
        <div className="form-group">
          <label>아이콘</label>
          <div className="icon-selector">
            {FOLDER_ICONS.map(({ value, label, Icon }) => (
              <button
                key={value}
                className={`icon-option ${folderIcon === value ? 'selected' : ''}`}
                onClick={() => setFolderIcon(value)}
                title={label}
                type="button"
              >
                <Icon size={20} style={{ color: folderColor }} />
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>색상</label>
          <div className="color-selector">
            {FOLDER_COLORS.map(color => (
              <button
                key={color}
                className={`color-option ${folderColor === color ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setFolderColor(color)}
                type="button"
                aria-label={color}
              />
            ))}
            <input
              type="color"
              value={folderColor}
              onChange={(e) => setFolderColor(e.target.value)}
              className="color-picker"
            />
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>취소</Button>
          <Button onClick={handleUpdate}>저장</Button>
        </div>
      </div>
    </div>
  );
}

export default FolderSidebar;